import { useMemo } from 'react'
import useSWR from 'swr'

import { useDependencies } from '@/components/providers/DependencyProvider'
import { Author, Brand, CustomField, Image, Maybe, Money, PageInfo, Product, ProductAvailable, ProductPreOrder, ProductUnavailable, Review, Reviews } from '@/types/bigcommerce'

type BigCommerceResponse = { __typename?: 'Query' } & {
	site: { __typename?: 'Site' } & {
		product?: Maybe<{
			__typename?: 'Product',
		} & Pick<Product, 'entityId' | 'sku' | 'name' | 'description'> & {
			availabilityV2: ProductAvailable | ProductPreOrder | ProductUnavailable,

			brand?: Maybe<{ __typename?: 'Brand' } & Pick<Brand, 'name'>>,

			images: {
				__typename?: 'ImageConnection',
			} & {
				edges?: Maybe<Array<Maybe<{
					__typename?: 'ImageEdge',
				} & {
					node: {
						__typename?: 'Image',
					} & Pick<Image, 'url' | 'altText' | 'isDefault'>,
				}>>>,
			},

			reviewSummary: {
				__typename?: 'Reviews',
			} & Pick<Reviews, 'numberOfReviews' | 'summationOfRatings'>,

			prices?: Maybe<{
				__typename?: 'Prices',
			} & {
				basePrice?: Maybe<{ __typename?: 'Money' } & Pick<Money, 'value'>>,
				price: { __typename?: 'Money' } & Pick<Money, 'value'>,
			}>,

			customFields: { __typename?: 'CustomFieldConnection' } & {
				edges?: Maybe<Array<Maybe<{
					__typename?: 'CustomFieldEdge',
				} & {
					node: { __typename?: 'CustomField' } & Pick<CustomField, 'name' | 'value'>,
				}>>>,
			},

			reviews: { __typename?: 'ReviewConnection' } & {
				edges?: Maybe<Array<Maybe<{
					__typename?: 'ReviewEdge',
				} & {
					node: { __typename?: 'Review' } & Pick<Review, 'rating' | 'text' | 'title'> & {
						author: { __typename?: 'Author' } & Pick<Author, 'name'>,
					},
				}>>>,

				pageInfo: {
					__typename?: 'PageInfo',
				} & Pick<PageInfo, 'hasNextPage' | 'hasPreviousPage' | 'startCursor' | 'endCursor'>,
			},
			relatedProducts: { __typename?: 'RelatedProductsConnection' } & {
				edges?: Maybe<Array<Maybe<{
					__typename?: 'RelatedProductsEdge',
				} & {
					node: {
						__typename?: 'Product',
					} & Pick<Product, 'name' | 'sku' | 'path'> & {
						brand?: Maybe<{ __typename?: 'Brand' } & Pick<Brand, 'name'>>,

						defaultImage?: Maybe<{
							__typename?: 'Image',
						} & Pick<Image, 'url' | 'altText'>>,

						images: {
							__typename?: 'ImageConnection',
						} & {
							edges?: Maybe<Array<Maybe<{
								__typename?: 'ImageEdge',
							} & {
								node: {
									__typename?: 'Image',
								} & Pick<Image, 'url' | 'isDefault'>,
							}>>>,
						},

						reviewSummary: {
							__typename?: 'Reviews',
						} & Pick<Reviews, 'numberOfReviews' | 'summationOfRatings'>,

						prices?: Maybe<{
							__typename?: 'Prices',
						} & {
							basePrice?: Maybe<{ __typename?: 'Money' } & Pick<Money, 'value'>>,
							price: { __typename?: 'Money' } & Pick<Money, 'value'>,
						}>,
					},
				}>>>,
			},
		}>,
	},
}

const query = `query productBySku($sku: String!) {
	site {
		product(sku: $sku) {
			entityId

			sku
			name
			description
			path
			brand {
				name
			}

			availabilityV2 {
				__typename
				status
				description
				... on ProductPreOrder {
					willBeReleasedAt {
						utc
					}
				}
				... on ProductUnavailable {
					message
				}
			}

			images {
				edges {
					node {
						url(width: 1280, height: 1280)
						altText
						isDefault
					}
				}
			}

			reviewSummary {
				numberOfReviews
				summationOfRatings
			}

			prices {
				basePrice {
					value
				}
				
				price {
					value
				}
			}

			customFields {
				edges {
					node {
						name
						value
					}
				}
			}

			relatedProducts {
				edges {
					node {
						brand {
							name
						}

						defaultImage {
							url(width: 256, height: 256)
							altText
						}

						images {
							edges {
								node {
									url(width: 256, height: 256)
									isDefault
								}
							}
						}

						name
						sku

						reviewSummary {
							numberOfReviews
							summationOfRatings
						}

						prices {
							basePrice {
								value
							}

							price {
								value
							}
						}
					}
				}
			}
		}
	}
}`

export default function useProduct(sku: string): { product: BigCommerceProduct | null, loading: boolean } {
	const { bigcommerceFetcher } = useDependencies()

	const { data } = useSWR<BigCommerceResponse>(
		[query, sku],
		async (document: string, sku: string) => bigcommerceFetcher(document, { sku }),
	)

	return useMemo(() => {
		if (data === undefined) {
			return { product: null, loading: true }
		}

		const { product } = data.site
		if (product === null || product === undefined) {
			return { product: null, loading: false }
		}

		let availability: Availability = {
			kind: 'available',
			description: product.availabilityV2.description,
		}
		if (product.availabilityV2.__typename === 'ProductPreOrder') {
			availability = {
				...availability,
				kind: 'preorder',
				availabilityDate: product.availabilityV2.willBeReleasedAt ? new Date(product.availabilityV2.willBeReleasedAt.utc) : null,
			}
		} else if (product.availabilityV2.__typename === 'ProductUnavailable') {
			availability = {
				...availability,
				kind: 'disabled',
				message: product.availabilityV2.message ?? null,
			}
		}

		const basePrice = product.prices?.basePrice?.value ?? null
		const price = product.prices?.price.value ?? null

		return {
			loading: false,
			product: {
				id: product.entityId,

				sku: product.sku,
				name: product.name,
				description: product.description,

				images: product.images.edges?.sort(edgeA => (edgeA!.node.isDefault ? -1 : 1)).map(edge => ({
					url: edge!.node.url,
					altText: edge!.node.altText === '' ? undefined : edge!.node.altText,
				})) ?? [],

				brand: product.brand?.name ?? null,

				reviewCount: product.reviewSummary.numberOfReviews,
				reviewTotal: product.reviewSummary.summationOfRatings,

				price: basePrice ?? price,
				sale_price: basePrice === price ? null : price,

				customFields: product.customFields.edges?.map(field => ({
					name: field!.node.name,
					value: field!.node.value,
				})) ?? [],

				availability,
			},
		}
	}, [data])
}
